import { useState, useEffect } from 'react';
import Button from '../shared/Button';
import { X } from 'lucide-react';
import { InfoTooltip } from '../shared/InfoTooltip';
import { DocumentService } from '../../services/DocumentService';
import { Toast } from '../shared/Toast';
import TextFieldWithCounter from '../shared/TextFieldWithCounter';
import { isValidTitleLength } from '../../utils/validators';

const styles = {
	section: 'fixed inset-0 z-[9999] flex items-center justify-center p-4',
	backDrop: 'absolute inset-0 bg-black/70',
	modal: 'relative z-10 w-full max-w-[500px] rounded-[18px] bg-white shadow-2xl p-[30px] overflow-visible',
	heading: 'text-25 font-bold text-dark-blue xl:mb-3 md:mb-1 leading-100',
	subHeading: 'text-gray text-sm xl:mb-6 md:mb-2',
	label: 'text-base leading-100 font-medium text-dark-blue',
	note: 'bg-lightPurple p-4 rounded-xl text-sm leading-21 text-dark-blue font-medium italic xl:mb-8 mb-3 flex flex-col',
	span: 'font-semibold text-base leading-100 text-purple xl:mb-2.5 mb-1'
};

async function renameWithRetry(documentId, title, { beforeRename } = {}) {
	if (typeof beforeRename === 'function') {
		await beforeRename();
	}

	try {
		return await DocumentService.renameDocument(documentId, title);
	} catch (firstError) {
		const status = firstError?.response?.status;
		// Retry once on network / 5xx — common when autosave content sync holds the doc write lock
		if (!status || status >= 500) {
			await new Promise((r) => setTimeout(r, 400));
			if (typeof beforeRename === 'function') {
				await beforeRename();
			}
			return await DocumentService.renameDocument(documentId, title);
		}
		throw firstError;
	}
}

export default function EditDocumentModal({
	isOpen,
	onClose,
	documentId,
	currentTitle,
	onBeforeUpdate,
	onAfterUpdate,
	onDocumentUpdated // Callback to refresh document data after update
}) {
	const [documentTitle, setDocumentTitle] = useState(currentTitle || '');
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setDocumentTitle(currentTitle || '');
		}
	}, [isOpen, currentTitle]);

	if (!isOpen) return null;

	const handleUpdateDocument = async () => {
		const trimmed = (documentTitle || '').trim();

		if (!trimmed) {
			Toast.error('Document title cannot be empty');
			return;
		}

		if (!isValidTitleLength(trimmed)) {
			Toast.error('Document title must be between 3 and 75 characters.');
			return;
		}

		if (!documentId) {
			Toast.error('Document ID is missing');
			return;
		}

		if (trimmed === (currentTitle || '').trim()) {
			Toast.info('No changes to save');
			onClose();
			return;
		}

		try {
			setIsLoading(true);

			await renameWithRetry(documentId, trimmed, { beforeRename: onBeforeUpdate });

			Toast.success('Document renamed successfully');

			if (onDocumentUpdated) {
				await onDocumentUpdated(trimmed);
			}

			onClose();
		} catch (error) {
			const message =
				error?.response?.data?.message ||
				error?.message ||
				'Failed to rename document';
			Toast.error(message);
		} finally {
			setIsLoading(false);
			if (typeof onAfterUpdate === 'function') {
				try {
					await onAfterUpdate();
				} catch {
					/* ignore */
				}
			}
		}
	};

	return (
		<div className={styles.section}>
			<div className={styles.backDrop} onClick={isLoading ? undefined : onClose} />

			<div className={styles.modal}>
				<button
					onClick={onClose}
					disabled={isLoading}
					className="absolute top-8 right-2 md:right-6 w-5 h-5 bg-dark-blue/40 hover:bg-dark-blue/80 rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
				>
					<X className="w-3 h-3 text-white" />
				</button>

				<h2 className={styles.heading}>Document Settings</h2>
				<p className={styles.subHeading}>Update document settings below</p>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				<div className="grid grid-cols-1 gap-6 xl:mb-4 mb-2">
					<div className="relative z-20 overflow-visible">
						<div className="flex items-center gap-2 xl:mb-3 mb-1 overflow-visible">
							<label htmlFor="document-title" className={styles.label}>
								Change document name
							</label>
							<InfoTooltip
								placement="bottom"
								text="Edit the name used to identify this document."
							/>
						</div>
						<TextFieldWithCounter
							className="focus:outline-none focus:border-purple"
							id="document-title"
							placeholder="Enter Document Name"
							value={documentTitle}
							onChange={(e) => setDocumentTitle(e.target.value)}
							minLength={3}
							maxLength={75}
						/>
					</div>
				</div>

				<div className="h-[1px] bg-[#F0F0F0] xl:mb-[25px] mb-2 mx-[-30px]" />

				<div className="flex xl:gap-3 gap-2">
					<button
						type="button"
						onClick={onClose}
						disabled={isLoading}
						className="flex-1 rounded-xl border border-lightGrey px-6 py-3 text-base font-medium text-dark-blue hover:bg-gray-50 disabled:opacity-50"
					>
						Cancel
					</button>
					<Button onClick={handleUpdateDocument} disabled={isLoading} className="flex-1">
						{isLoading ? 'Updating...' : 'Update Document'}
					</Button>
				</div>
			</div>
		</div>
	);
}
